// frontend/src/hooks/useLipSync.ts
//
// Web Audio API tabanlı dudak senkronizasyon hook'u.
// SpeechSynthesisUtterance tarayıcı güvenliği nedeniyle AudioContext'e
// doğrudan bağlanamadığından, boundary eventleri + konuşma ritmi
// simülasyonu kullanarak gerçekçi ağız hareketi üretiriz.
//
// Döndürülen `mouthOpenness` değeri 0 (kapalı) ile 1 (tam açık) arasındadır.
// SVG path veya CSS custom property olarak kullanılabilir.

import { useRef, useState, useCallback, useEffect } from 'react';

interface LipSyncState {
  mouthOpenness: number;   // 0 = kapalı, 1 = tam açık
  isSpeaking: boolean;
}

interface UseLipSyncReturn extends LipSyncState {
  startSpeaking: () => void;
  stopSpeaking: () => void;
  onBoundary: () => void;   // Her kelime sınırında çağrılır
}

// Konuşma sesine yakın frekans tabanlı amplitüd dalgalanmaları simüle eder.
// İnsan konuşması ~2-8 Hz arası ağız açma/kapama ritmine sahiptir.
const SPEECH_FREQ_HZ = 4.5; // ortalama hece ritmi
const BOUNDARY_BURST_MS = 120; // kelime başında kısa ani açılma süresi

export function useLipSync(): UseLipSyncReturn {
  const [mouthOpenness, setMouthOpenness] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const animFrameRef = useRef<number>(0);
  const speakingRef = useRef(false);
  const startTimeRef = useRef(0);
  const boundaryTimeRef = useRef(0);

  const animate = useCallback(() => {
    if (!speakingRef.current) {
      // Konuşma durdu — ağzı yumuşakça kapat
      setMouthOpenness(prev => {
        const next = prev * 0.85;
        return next < 0.01 ? 0 : next;
      });
      if (animFrameRef.current) {
        animFrameRef.current = requestAnimationFrame(animate);
      }
      return;
    }

    const now = performance.now();
    const elapsed = (now - startTimeRef.current) / 1000; // saniye cinsinden

    // Ana konuşma dalgası: sinüs tabanlı hece ritmi
    const primaryWave = Math.sin(elapsed * SPEECH_FREQ_HZ * Math.PI * 2);

    // İkinci harmonik: daha doğal ses için hafif titreşim
    const secondaryWave = Math.sin(elapsed * SPEECH_FREQ_HZ * 1.7 * Math.PI * 2) * 0.3;

    // Ham sinyal (−1 ile +1 arası) → 0 ile 1 arası normalize et
    const raw = (primaryWave + secondaryWave) / 1.3;
    const normalized = (raw + 1) / 2; // 0..1

    // Kelime sınırı burst: yeni kelimede ağzı ani açmak daha doğal görünür
    const timeSinceBoundary = now - boundaryTimeRef.current;
    const boundaryBoost = timeSinceBoundary < BOUNDARY_BURST_MS
      ? (1 - timeSinceBoundary / BOUNDARY_BURST_MS) * 0.4
      : 0;

    // Minimum açıklık (konuşurken hiç kapanmasın) + burst etkisi
    const final = Math.min(1, Math.max(0.15, normalized * 0.7 + boundaryBoost));

    setMouthOpenness(final);
    animFrameRef.current = requestAnimationFrame(animate);
  }, []);

  const startSpeaking = useCallback(() => {
    speakingRef.current = true;
    startTimeRef.current = performance.now();
    setIsSpeaking(true);
    animFrameRef.current = requestAnimationFrame(animate);
  }, [animate]);

  const stopSpeaking = useCallback(() => {
    speakingRef.current = false;
    setIsSpeaking(false);
    // Animasyon döngüsü kapanmaya devam eder (yumuşak kapanış için)
    // ~500ms sonra tamamen durdurulur
    setTimeout(() => {
      if (!speakingRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        setMouthOpenness(0);
      }
    }, 500);
  }, []);

  const onBoundary = useCallback(() => {
    // Kelime sınırında anlık burst tetikle
    boundaryTimeRef.current = performance.now();
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      // Component unmount oldu — animasyonu ve konuşma durumunu sıfırla
      speakingRef.current = false;
      cancelAnimationFrame(animFrameRef.current);
      setMouthOpenness(0);
      setIsSpeaking(false);
    };
  }, []);

  return { mouthOpenness, isSpeaking, startSpeaking, stopSpeaking, onBoundary };
}
